import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import apis from '../../utils/apis';
import { bulkAssignSeats, assignUserToAddon, revokeUserFromAddon, changePlan } from '../../store/actions/PlanActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { PiShieldCheckLight, PiPuzzlePieceLight, PiCheckCircleLight, PiSpinnerGapLight, PiXCircleLight } from 'react-icons/pi';
import { formatDateTime } from '../../utils/functions';
import { toErrorText } from '../../utils/errorMessage';

interface BasePlan {
  org_license_id: number;
  license_id: number;
  name: string;
  display_name: string;
  type: string;
  price_monthly: number | null;
  feature_gates?: { sections?: string[]; ai_features?: string[] };
}

interface AddonOption {
  license_id: number;
  org_license_id?: number;  // Set if org already has an OrganizationLicense row for this addon
  name: string;
  display_name: string | null;
  price_monthly: number | null;
  is_active_for_user?: boolean;
}

interface UserLicenseInfo {
  has_base_seat: boolean;
  base_plan: any | null;
  user_active_addons: any[];
}

interface AssignLicensePanelProps {
  userIds: number[];
  userNames: string[];
  onClose: () => void;
  onComplete?: () => void;
}

const AssignLicensePanel: React.FC<AssignLicensePanelProps> = ({
  userIds,
  userNames,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [basePlans, setBasePlans] = useState<BasePlan[]>([]);
  const [addons, setAddons] = useState<AddonOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<BasePlan | null>(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState<Set<number>>(new Set());
  const [assigning, setAssigning] = useState(false);
  const [done, setDone] = useState(false);
  const [userLicense, setUserLicense] = useState<UserLicenseInfo | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const isSingleUser = userIds.length === 1;
  const isStarter = selectedPlan?.name === 'pons_starter';
  const isManageMode = isSingleUser && userLicense?.has_base_seat === true;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [assignRes, plansRes] = await Promise.all([
        apis.get('/licensing/assignments'),
        apis.get('/licensing/plans'),
      ]);

      const assignData = assignRes.data?.data || assignRes.data || {};
      const catalogData = plansRes.data?.data || plansRes.data || {};

      // Build base plans
      const base = assignData?.base_plan;
      const orgAddons = assignData?.addons || [];
      const plans: BasePlan[] = [];

      if (base) {
        plans.push({
          org_license_id: base.org_license_id,
          license_id: base.license_id,
          name: base.name,
          display_name: base.display_name || base.name,
          type: base.type,
          price_monthly: null,
          feature_gates: undefined,
        });
      }

      const basePlansFromCatalog = catalogData?.base_plans || catalogData?.plans || [];
      for (const p of basePlansFromCatalog) {
        if (p.type === 'base') {
          const existing = plans.find(bp => bp.license_id === p.id);
          if (existing) {
            existing.price_monthly = p.price_monthly;
            existing.feature_gates = p.feature_gates;
          } else {
            plans.push({
              org_license_id: 0,
              license_id: p.id,
              name: p.name,
              display_name: p.display_name || p.name,
              type: p.type,
              price_monthly: p.price_monthly,
              feature_gates: p.feature_gates,
            });
          }
        }
      }

      // Build addon list from catalog (source of truth for what's available).
      // Cross-reference with org assignments to get org_license_id for each.
      const catalogAddons = catalogData?.addons || [];

      // Map org addon license_id → org_license_id (so we can assign via the existing row)
      const orgAddonMap = new Map<number, number>();
      for (const a of orgAddons) {
        if (!orgAddonMap.has(a.license_id)) {
          orgAddonMap.set(a.license_id, a.org_license_id);
        }
      }

      // For single user, fetch their current license data
      let userActiveAddonIds = new Set<number>();
      if (isSingleUser) {
        try {
          const userRes = await apis.get(`/admin/users/${userIds[0]}/licenses`);
          if (userRes.data?.success) {
            const ud = userRes.data.data;
            setUserLicense({
              has_base_seat: ud.has_base_seat,
              base_plan: ud.base_plan,
              user_active_addons: ud.user_active_addons || [],
            });
            userActiveAddonIds = new Set((ud.user_active_addons || []).map((a: any) => a.license_id));
          }
        } catch { /* non-critical */ }
      }

      // One addon per catalog entry, no duplicates
      const addonOptions: AddonOption[] = catalogAddons.map((a: any) => ({
        license_id: a.id,
        org_license_id: orgAddonMap.get(a.id),
        name: a.name,
        display_name: a.display_name,
        price_monthly: a.price_monthly,
        is_active_for_user: userActiveAddonIds.has(a.id),
      }));

      setAddons(addonOptions);
      setBasePlans(plans);

      if (plans.length === 1) {
        setSelectedPlan(plans[0]);
      }
    } catch (err) {
      console.error('Failed to load license data:', err);
    } finally {
      setLoading(false);
    }
  }, [isSingleUser, userIds]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleAddon = (licenseId: number) => {
    setSelectedAddonIds(prev => {
      const next = new Set(prev);
      if (next.has(licenseId)) next.delete(licenseId);
      else next.add(licenseId);
      return next;
    });
  };

  // Manage mode: assign a single addon to the user (per-user purchase)
  const handleAssignAddon = async (addon: AddonOption) => {
    setActionLoading(addon.license_id);
    try {
      if (addon.org_license_id) {
        const result = await dispatch(assignUserToAddon({ org_license_id: addon.org_license_id, user_id: userIds[0] }));
        if (result && result.success !== false) {
          dispatch(showFeedbackModal({ showModal: true, message: t('Add-on assigned'), modalType: 'success', duration: 3000 }));
          await loadData();
          onComplete?.();
        } else if (result && result.success === false) {
          // Backend returned 200 but with success:false (shouldn't happen, but handle it)
          dispatch(showFeedbackModal({ showModal: true, message: toErrorText(result.error) || t('Failed to assign add-on'), modalType: 'error', duration: 4000 }));
        }
        // If result is null, handleApiError in the action already showed the error toast
      } else {
        // Addon not yet purchased by org - show info
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('This add-on needs to be purchased for the organization first. Assign it from the Licenses page.'),
          modalType: 'info',
          duration: 5000,
        }));
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Manage mode: change the org's base plan
  const handleChangePlan = async (targetPlan: BasePlan) => {
    setActionLoading(targetPlan.license_id);
    try {
      const result = await dispatch(changePlan({ new_license_id: targetPlan.license_id, billing_period: 'monthly' }));
      if (result) {
        await loadData();
        onComplete?.();
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Manage mode: revoke an addon from the user
  const handleRevokeAddon = async (addon: AddonOption) => {
    if (!addon.org_license_id) return;
    setActionLoading(addon.license_id);
    try {
      const result = await dispatch(revokeUserFromAddon({ org_license_id: addon.org_license_id, user_id: userIds[0] }));
      if (result) {
        dispatch(showFeedbackModal({ showModal: true, message: t('Add-on revoked'), modalType: 'success', duration: 3000 }));
        await loadData();
        onComplete?.();
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Assign mode: bulk assign base seats + addons
  const handleAssign = async () => {
    if (!selectedPlan) return;
    setAssigning(true);
    try {
      const addonOrgLicenseIds: number[] = [];
      if (isStarter && selectedAddonIds.size > 0) {
        for (const addonLicenseId of selectedAddonIds) {
          const addon = addons.find(a => a.license_id === addonLicenseId);
          if (addon?.org_license_id) {
            addonOrgLicenseIds.push(addon.org_license_id);
          }
        }
      }

      const result = await dispatch(bulkAssignSeats(userIds, addonOrgLicenseIds.length > 0 ? addonOrgLicenseIds : undefined));
      if (!result) {
        dispatch(showFeedbackModal({ showModal: true, message: t('Failed to assign seats'), modalType: 'error', duration: 4000 }));
        return;
      }

      setDone(true);
      const addonCount = result.addon_results?.filter((r: any) => r.success).length ?? 0;
      dispatch(showFeedbackModal({
        showModal: true,
        message: isSingleUser
          ? addonCount > 0
            ? t('License + {{count}} add-on(s) assigned to {{name}}', { count: addonCount, name: userNames[0] })
            : t('License assigned to {{name}}', { name: userNames[0] })
          : t('{{count}} seats assigned successfully', { count: result.assigned_count ?? userIds.length }),
        modalType: 'success',
        duration: 3000,
      }));

      onComplete?.();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, message: t('Assignment failed'), modalType: 'error', duration: 4000 }));
    } finally {
      setAssigning(false);
    }
  };

  // Calculate total monthly cost for this user
  const totalMonthly = (() => {
    let total = 0;
    if (isManageMode && userLicense?.base_plan) {
      // Use catalog price for base plan (backend may not return it)
      const bpCatalog = basePlans.find(p => p.license_id === userLicense.base_plan?.license_id);
      total += bpCatalog?.price_monthly ?? userLicense.base_plan.price_monthly ?? 0;
      // Add each active addon's catalog price
      for (const addon of userLicense.user_active_addons || []) {
        const addonCatalog = addons.find(a => a.license_id === addon.license_id);
        total += addonCatalog?.price_monthly ?? addon.price_monthly ?? 0;
      }
    } else if (selectedPlan?.price_monthly) {
      total += selectedPlan.price_monthly;
      for (const addonId of selectedAddonIds) {
        const addon = addons.find(a => a.license_id === addonId);
        if (addon?.price_monthly) total += addon.price_monthly;
      }
    }
    return total;
  })();

  if (loading) {
    return (
      <div className="p-6 space-y-4 animate-fadePulse">
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex flex-col items-center justify-center p-8 gap-3">
        <PiCheckCircleLight className="h-10 w-10 text-success-500" />
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {isSingleUser ? t('License assigned') : t('{{count}} seats assigned', { count: userIds.length })}
        </p>
        <Button variant="primary" size="small" onClick={onClose}>{t('Done')}</Button>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // MANAGE MODE - user already has a seat
  // ═══════════════════════════════════════════════════
  if (isManageMode && userLicense) {
    const bp = userLicense.base_plan;
    const activeAddons = userLicense.user_active_addons || [];
    const currentPlanIsStarter = bp?.name === 'pons_starter';
    const currentPlanIsFull = bp?.name === 'pons_full';
    const seenIds = new Set<number>();
    const dedupedActiveAddons = activeAddons.filter((a: any) => {
      if (seenIds.has(a.license_id)) return false;
      seenIds.add(a.license_id);
      return true;
    });
    const availableAddons = addons.filter(a => !seenIds.has(a.license_id));

    // Plan change options: other base plans the org could switch to
    const otherPlans = basePlans.filter(p => p.license_id !== bp?.license_id && p.type === 'base');
    const fullPlan = basePlans.find(p => p.name === 'pons_full');
    const fullPlanPrice = fullPlan?.price_monthly ?? 199;

    // Nudge: if addons push close to Full plan price
    const shouldNudgeFull = currentPlanIsStarter && !currentPlanIsFull && totalMonthly > 0 && totalMonthly >= fullPlanPrice * 0.7;

    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* User */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
              {t('User')}
            </p>
            <span className="text-sm font-medium text-gray-900 dark:text-white">{userNames[0]}</span>
          </div>

          {/* Current plan + change options */}
          {bp && (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                {t('Current plan')}
              </p>
              <div className="rounded-lg border border-info-200 dark:border-info-700 bg-info-50 dark:bg-info-900/10 p-3">
                <div className="flex items-center gap-2">
                  <PiShieldCheckLight className="h-4 w-4 text-info-600 dark:text-info-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{bp.display_name || bp.name}</span>
                  <Badge color={bp.type === 'base' ? 'blue' : bp.type === 'trial' ? 'yellow' : 'gray'}>
                    {(bp.type || '').charAt(0).toUpperCase() + (bp.type || '').slice(1)}
                  </Badge>
                  {bp.price_monthly != null && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 ms-auto">{'\u20AC'}{bp.price_monthly}/{t('mo')}</span>
                  )}
                </div>
                {bp.license_expires_at && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {t('Expires')} {formatDateTime(bp.license_expires_at)}
                  </p>
                )}
              </div>

              {/* Other plans to switch to */}
              {otherPlans.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {otherPlans.map((plan) => {
                    const isUpgrade = (bp.sort_order ?? 0) < (plan.feature_gates ? 99 : 0) || (plan.price_monthly ?? 0) > (bp.price_monthly ?? 0);
                    const isLoading = actionLoading === plan.license_id;
                    return (
                      <div key={plan.license_id} className="glass flex items-center gap-2 px-3 py-2 rounded-lg">
                        <PiShieldCheckLight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                        <span className="text-xs font-medium text-gray-900 dark:text-white flex-1 truncate">
                          {plan.display_name}
                        </span>
                        {plan.price_monthly != null && (
                          <span className="text-xs text-gray-400">{'\u20AC'}{plan.price_monthly}/{t('mo')}</span>
                        )}
                        <Button
                          variant={isUpgrade ? 'primary' : 'outline'}
                          size="small"
                          onClick={() => handleChangePlan(plan)}
                          disabled={isLoading}
                          isLoading={isLoading}
                        >
                          {isUpgrade ? t('Upgrade') : t('Switch')}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Full plan nudge */}
          {shouldNudgeFull && fullPlan && (
            <div className="flex items-center gap-3 rounded-lg border border-warning-200 dark:border-warning-700 bg-warning-50 dark:bg-warning-900/10 p-3">
              <PiShieldCheckLight className="h-4 w-4 text-warning-600 dark:text-warning-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 dark:text-white">
                  {t('Consider PONS Full')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('This user costs {{current}}/mo with add-ons. PONS Full is {{full}}/mo and includes everything.', {
                    current: `\u20AC${totalMonthly.toFixed(0)}`,
                    full: `\u20AC${fullPlanPrice}`,
                  })}
                </p>
              </div>
              <Button
                variant="primary"
                size="small"
                onClick={() => handleChangePlan(fullPlan)}
                disabled={actionLoading === fullPlan.license_id}
                isLoading={actionLoading === fullPlan.license_id}
              >
                {t('Upgrade')}
              </Button>
            </div>
          )}

          {/* Active add-ons (deduplicated) */}
          {dedupedActiveAddons.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                {t('Active add-ons')} ({dedupedActiveAddons.length})
              </p>
              <div className="space-y-1.5">
                {dedupedActiveAddons.map((addon: any) => {
                  const price = addon.price_monthly ?? addons.find(a => a.license_id === addon.license_id)?.price_monthly;
                  const isLoading = actionLoading === addon.license_id;
                  return (
                    <div key={addon.org_license_id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-success-200 dark:border-success-700 bg-success-50 dark:bg-success-900/10">
                      <PiPuzzlePieceLight className="h-3.5 w-3.5 text-success-600 dark:text-success-400 flex-shrink-0" />
                      <span className="text-xs font-medium text-gray-900 dark:text-white flex-1 truncate">
                        {addon.display_name || addon.name}
                      </span>
                      {price != null && (
                        <span className="text-xs text-gray-400">{'\u20AC'}{price}/{t('mo')}</span>
                      )}
                      <button
                        onClick={() => handleRevokeAddon({ ...addon, price_monthly: price, source: 'org' })}
                        disabled={isLoading}
                        className="text-xs text-danger-500 hover:text-danger-700 dark:text-danger-400 underline flex-shrink-0"
                      >
                        {isLoading ? t('...') : t('Revoke')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Available add-ons (Starter only) - each is a per-user purchase */}
          {currentPlanIsStarter && availableAddons.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                {t('Available add-ons')}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                {t('Each add-on is billed per user per month')}
              </p>
              <div className="space-y-1.5">
                {availableAddons.map((addon) => {
                  const isLoading = actionLoading === addon.license_id;
                  return (
                    <div key={addon.license_id} className="glass flex items-center gap-2 px-3 py-2 rounded-lg">
                      <PiPuzzlePieceLight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-xs font-medium text-gray-900 dark:text-white flex-1 truncate">
                        {addon.display_name || addon.name}
                      </span>
                      {addon.price_monthly != null && (
                        <span className="text-xs text-gray-400">{'\u20AC'}{addon.price_monthly}/{t('mo')}</span>
                      )}
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => handleAssignAddon(addon)}
                        disabled={isLoading}
                        isLoading={isLoading}
                      >
                        {t('Add')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Full plan notice - no addons needed */}
          {currentPlanIsFull && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-info-50 dark:bg-info-900/10 border border-info-200 dark:border-info-700 text-xs text-info-700 dark:text-info-300">
              <PiShieldCheckLight className="h-4 w-4 flex-shrink-0" />
              <span>{t('PONS Full includes all features. No add-ons needed.')}</span>
            </div>
          )}

          {/* Monthly total */}
          {totalMonthly > 0 && (
            <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('Total per month')}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{'\u20AC'}{totalMonthly.toFixed(2)}/{t('mo')}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex items-center justify-end">
          <Button variant="outline" size="small" onClick={onClose}>{t('Close')}</Button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // ASSIGN MODE - user(s) need a seat
  // ═══════════════════════════════════════════════════
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-5 space-y-5">

        {/* Users being assigned */}
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
            {isSingleUser ? t('Assigning to') : t('Assigning to {{count}} users', { count: userIds.length })}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {userNames.slice(0, 10).map((name, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                {name}
              </span>
            ))}
            {userNames.length > 10 && (
              <span className="text-xs text-gray-400">+{userNames.length - 10} {t('more')}</span>
            )}
          </div>
        </div>

        {/* Plan selection */}
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
            {t('Select plan')}
          </p>
          <div className="space-y-2">
            {basePlans.map((plan) => (
              <button
                key={plan.license_id}
                onClick={() => { setSelectedPlan(plan); setSelectedAddonIds(new Set()); }}
                className={`w-full text-start rounded-lg border p-3 transition-colors ${
                  selectedPlan?.license_id === plan.license_id
                    ? 'border-info-500 bg-info-50 dark:bg-info-900/20 dark:border-info-400'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-2">
                  <PiShieldCheckLight className={`h-4 w-4 ${selectedPlan?.license_id === plan.license_id ? 'text-info-600 dark:text-info-400' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{plan.display_name}</span>
                  <Badge color={plan.type === 'base' ? 'blue' : 'gray'}>
                    {plan.type.charAt(0).toUpperCase() + plan.type.slice(1)}
                  </Badge>
                  {plan.price_monthly != null && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 ms-auto">
                      {'\u20AC'}{plan.price_monthly}/{t('mo')}/user
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Add-ons (for Starter) */}
        {isStarter && addons.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('Add-ons')} <span className="normal-case font-normal">({t('optional')})</span>
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
              {t(isSingleUser ? 'Select add-ons to grant additional features to this user' : 'Select add-ons to grant to the selected users')}
            </p>
            <div className="space-y-1.5">
              {addons.map((addon) => {
                const selected = selectedAddonIds.has(addon.license_id);
                return (
                  <button
                    key={addon.license_id}
                    onClick={() => toggleAddon(addon.license_id)}
                    className={`w-full text-start rounded-lg border px-3 py-2 transition-colors flex items-center gap-2 ${
                      selected
                        ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-500'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      readOnly
                      className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 pointer-events-none"
                    />
                    <PiPuzzlePieceLight className={`h-3.5 w-3.5 ${selected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
                    <span className="text-xs font-medium text-gray-900 dark:text-white flex-1 truncate">
                      {addon.display_name || addon.name}
                    </span>
                    {addon.price_monthly != null && (
                      <span className="text-xs text-gray-400">{'\u20AC'}{addon.price_monthly}/{t('mo')}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Monthly total preview */}
        {totalMonthly > 0 && (
          <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('Est. total per user/month')}</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">{'\u20AC'}{totalMonthly.toFixed(2)}/{t('mo')}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex items-center justify-end gap-2">
        <Button variant="outline" size="small" onClick={onClose} disabled={assigning}>
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleAssign}
          disabled={!selectedPlan || assigning}
        >
          {assigning
            ? t('Assigning...')
            : selectedAddonIds.size > 0
              ? t('Assign plan + {{count}} add-on(s)', { count: selectedAddonIds.size })
              : isSingleUser
                ? t('Assign license')
                : t('Assign {{count}} seats', { count: userIds.length })
          }
        </Button>
      </div>
    </div>
  );
};

export default AssignLicensePanel;
